import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialPostUserHeader.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CustomImage } from "./CustomImage.js";
import { UserRated } from "./UserRated";
import { useNavigate } from "./Router";
import { FaRegClock, FaRegCopy, FaTrashAlt, FaFlag, FaEllipsisH, FaPen } from 'react-icons/fa';

const SocialPostUserHeader = ({ user_id, post, underMessage, canDelete = false, onDelete, canEdit = false, onEdit }) => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [postTime, setPostTime] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [user, setUser] = useState(null);
  
  const optionsRef = useRef(null);

  const clickUserBy = () => {
    navigate("/user/" + user_id);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowOptions(false);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
    setShowOptions(false);
  };

  const copyLink = () => {
    if (!post) return;
    
    const link = `https://peupleqc.com/post/${post.id}${user ? `?ref=${user.user_id}` : ''}`;
    navigator.clipboard.writeText(link)
      .then(() => {
        alert("Lien copié avec succès!");
      })
      .catch(err => {
        console.error('Erreur lors de la copie: ', err);
      });
    
    setShowOptions(false);
  };

  const reportPost = () => {
    sdk.models.post.signal({id: post.id});
    alert("Merci, nous allons regarder ça.");
    setShowOptions(false);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const postDate = new Date(timestamp * 1000);
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    if (diffInSeconds < 60) {
      return "À l'instant";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `Il y a ${months} mois`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `Il y a ${years} an${years > 1 ? 's' : ''}`;
    }
  };

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("liberium-token");
        if (token) {
          const myself = localStorage.getItem("myself");
          if (myself) {
            setUser(JSON.parse(myself));
          } else {
            const response = await sdk.models.PeupleUser.getMySelf({});
            if (response && !response.error) {
              setUser(response);
              localStorage.setItem("myself", JSON.stringify(response));
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'utilisateur actuel:", err);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userResponse = await sdk.models.PeupleUser.getOne(user_id);
        if (userResponse) {
          setAvatarUrl(userResponse.avatar 
            ? `https://peupleqc.com/s/peupleqc-upload/${userResponse.avatar}` 
            : "https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
          setUserName(`${userResponse.first_name || ''} ${userResponse.last_name || ''}`.trim() || "Utilisateur");
          
          // Détecter les rôles spéciaux
          if (userResponse.user_id === 2) {
            setUserRole("Fondateur");
            setIsVerified(true);
          } else if (userResponse.user_id === 1435) {
            setUserRole("Co-Fondateur");
            setIsVerified(true);
          } else if (userResponse.admin === 1) {
            setUserRole("Administrateur");
            setIsVerified(true);
          }
          
          // Formatter la date du post
          if (post && post.time) {
            setPostTime(formatTimestamp(post.time));
          }

          setError("");
        } else {
          setError("Utilisateur non trouvé");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des données de l'utilisateur:", err);
        setError("Erreur lors du chargement");
      } finally {
        setIsLoading(false);
      }
    };

    if (user_id) {
      fetchUserData();
    }
  }, [user_id, post]);

  const getFullDate = () => {
    if (!post || !post.time) return "";
    
    const date = new Date(post.time * 1000);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Close options menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptions && optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showOptions]);

  if (error) {
    return <div className="user-header-error">{error}</div>;
  }

  return (
    <div className="post-user-header">
      <div className="user-profile" onClick={clickUserBy}>
        <div className="user-avatar">
          {isLoading ? (
            <div className="avatar-placeholder"></div>
          ) : (
            <CustomImage 
              src={avatarUrl} 
              className="avatar-image" 
              alt={userName || "Utilisateur"} 
            />
          )}
        </div>
        
        <div className="user-info">
          <div className="user-name-container">
            <h4 className="user-name">
              {userName}
              <UserRated user_id={user_id}/>
              {1===2 && isVerified && (
                <span className="verified-badge" title="Compte vérifié">
                  <svg viewBox="0 0 24 24" fill="#1877F2" width="16" height="16">
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-1.9 14.7l-4.5-4.5 1.4-1.4 3.1 3.1 6.5-6.5 1.4 1.4-7.9 7.9z"></path>
                  </svg>
                </span>
              )}
            </h4>
            {userRole && <span className="user-role">{userRole}</span>}
          </div>
          
          <div className="post-meta">
            <span className="post-time" title={getFullDate()}>
              <FaRegClock className="time-icon" />
              {postTime}
            </span>
            {underMessage && (
              <span className="post-context">{underMessage}</span>
            )}
          </div>
        </div>
      </div>

      <div className="post-options" ref={optionsRef}>
        <div 
          className="post-options-trigger" 
          onClick={(e) => {
            e.stopPropagation();
            setShowOptions(!showOptions);
          }}
          aria-label="Options de publication"
        >
          <FaEllipsisH />
        </div>

        {showOptions && (
          <div className="options-menu">
            {canEdit && (
              <div className="menu-item" onClick={handleEdit}>
                <FaPen className="menu-item-icon" />
                Modifier
              </div>
            )}
            
            <div className="menu-item" onClick={copyLink}>
              <FaRegCopy className="menu-item-icon" />
              Copier le lien
            </div>
            
            <div className="menu-item" onClick={reportPost}>
              <FaFlag className="menu-item-icon" />
              Signaler
            </div>
            
            {canDelete && (
              <div className="menu-item menu-item-delete" onClick={handleDelete}>
                <FaTrashAlt className="menu-item-icon" />
                Supprimer
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { SocialPostUserHeader };